import { get, isFinite } from 'lodash';
import moment from 'moment';

import { Employment } from '@funnels/types';
import { validateDate } from '@funnels/utils';
import { FIELD_PATHS, FIELD_VALUES } from '@funnels/constants';

function getEmploymentWithOldestStartDate(employments: Employment[]) {
  if (employments.length === 1) {
    return employments[0];
  }

  // sort mutates arrays so shallow copy first
  const sorted = [...employments].sort((a, b) => {
    const startDateA = get({ employments: [a] }, FIELD_PATHS.EMPLOYMENT_START_DATE(0));
    const startDateB = get({ employments: [b] }, FIELD_PATHS.EMPLOYMENT_START_DATE(0));

    return Number(moment(startDateA).format('YYYYMMDD')) - Number(moment(startDateB).format('YYYYMMDD'));
  });

  return sorted[0];
}

export function validateOldestStartDate(employments: Employment[]) {
  if (!employments.length) {
    return { isLessThanThreeYears: true, isOldestStartDateInvalid: true };
  }

  const employment = getEmploymentWithOldestStartDate(employments);
  const startDate = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_START_DATE(0));

  if (!validateDate(startDate)) {
    return { isLessThanThreeYears: true, isOldestStartDateInvalid: true };
  }

  const isCurrentEmployed = employments.some((employment) =>
    [FIELD_VALUES.PRIMARY, FIELD_VALUES.SECONDARY].includes(
      get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_TYPE(0))
    )
  );

  const diff = moment().diff(moment(startDate), 'years');
  // At least one employment history has a start date for more than 3 years from now and there is at least one current employment
  return { isLessThanThreeYears: !isFinite(diff) || diff < 3 || !isCurrentEmployed, isOldestStartDateInvalid: false };
}

export function validateEmploymentHistory(employments: Employment[]) {
  if (!employments.length) {
    return false;
  }

  const validValuesCount = employments.filter((employment) => {
    const abn = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_ABN(0));
    const businessName = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_BUSINESS_NAME(0));
    const address = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_EMPLOYER_ADDRESS_FORMATTED(0));
    const type = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_TYPE(0));
    const employmentType = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(0));
    const isCurrent = type === FIELD_VALUES.PRIMARY || type === FIELD_VALUES.SECONDARY;
    const isPrevious = type === FIELD_VALUES.PREVIOUS;
    const role = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_ROLE_ANZSCO_CODE(0));
    const isForeignEmployed = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_FOREIGN_EMPLOYED(0));
    const isNotWorking = employmentType === FIELD_VALUES.NOT_WORKING;
    const startDate = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_START_DATE(0));
    const endDate = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_END_DATE(0));

    if (isCurrent && isNotWorking) {
      return startDate;
    }

    if (isCurrent) {
      return (isForeignEmployed || abn) && businessName && address && role;
    }

    if (isPrevious && isNotWorking) {
      return startDate && endDate;
    }

    return (isForeignEmployed || abn) && businessName && role;
  }).length;

  return validValuesCount === employments.length;
}
